import {
  createAsyncThunk,
  createSlice,
  Draft,
  PayloadAction
} from "@reduxjs/toolkit";
import { HttpCodes } from "../../../constants/app.constant";

import { IFunctionsList } from "pages/JobPricing/Functions/types";
import { IGradesList } from "pages/JobPricing/Grades/types";
import { IJobsList } from "pages/JobPricing/Jobs/types";
import { getList } from "../../../services/JobPricing/JobsListService";
import { getPricingFiltersThunk } from "../jobPricingDetailsSlice";

export enum SJPRListTypes {
  JOBS = "jobs",
  FUNCTION = "function",
  GRADES = "grades"
}

export interface IFilter {
  jobfamily: string[] | number[];

  jobsubfamily: string[] | number[];

  industry: string[] | number[];

  incumbent: string[] | number[];

  region: string[] | number[];

  clientownership: string[] | number[];

  numofemp: string[] | number[];

  revenuerangedesc: string[] | number[];

  grade: string[] | number[];

  customGradeId: string;
}

export interface IJobPricingSlice {
  list: IFunctionsList[] | IJobsList[] | IGradesList[] | any;
  filtersList?: any;
  filtersCount?: any;
  isListloading: boolean;
  isFiltersloading: boolean;
  category?: string;
  filters?: IFilter;
  searchKey?: string;
  isApiFailed?: boolean;
  responseMessage?: string;
  peerGroupId?: number; selectedPeerGroup?: { key: string; label: string } | null;
}

const initialState: IJobPricingSlice = {
  list: [],
  isListloading: false,
  filtersList: {},
  isFiltersloading: false,
  category: "",
  filters: {
    jobfamily: [],
    jobsubfamily: [],
    industry: [],
    incumbent: [],
    region: [],
    clientownership: [],
    numofemp: [],
    revenuerangedesc: [],
    grade: [],
    customGradeId: ""
  },
  filtersCount: {
    jobfamily: 0,
    jobsubfamily: 0,
    industry: 0,
    incumbent: 0,
    region: 0,
    clientownership: 0,
    numofemp: 0,
    revenuerangedesc: 0,
    grade: 0
  },
  searchKey: "",
  isApiFailed: false,
  responseMessage: "",
  selectedPeerGroup: null,
};

// single Job pricing main search thunk
export const JobPricingListThunk = createAsyncThunk(
  "sjprList/Getlist",
  async (payLoad: any, thunkApi: any) => {
    try {
      const { data } = await getList(payLoad);
      if (data.responseCode == HttpCodes.SUCCESS) {
        return {
          type: payLoad.category,
          data: data.data || []
        };
      } else if (data.responseCode == HttpCodes.SUCCESS_204) {
        return {
          type: payLoad.category,
          data: [],
          responseMessage: data.responseMessage
        };
      } else {
        return { type: payLoad.category, data: [] };
      }
    } catch (e) {
      console.log(e);
      return thunkApi.rejectWithValue(e);
    }
  }
);

export const jobPricingListSlice = createSlice({
  name: "sjprList",
  initialState,
  reducers: {
    getFilters: (state) => {
      state.filters = initialState.filters;
    },
    updateFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload
      };
    },
    updatePeerGroupId: (state, action) => {
      state.peerGroupId = action.payload;
    },
    resetPeerGroupId: (state) => {
      state.peerGroupId = undefined;
    },
    resetJobPricingList: (state) => {
      state.list = [];
      // state.filtersList = {};
    },
    resetFilters: (state) => {
      state.filters = {
        jobfamily: [],
        jobsubfamily: [],
        industry: [],
        incumbent: [],
        region: [],
        clientownership: [],
        numofemp: [],
        revenuerangedesc: [],
        grade: [],
        customGradeId: ""
      };
    },
    updateFiltersCount: (state, action) => {
      state.filtersCount = {
        ...state.filtersCount,
        ...action.payload
      };
    },
    resetFiltersCount: (state) => {
      state.filtersCount = initialState?.filtersCount;
    },
    updatedSearchKey: (state, action) => {
      state.searchKey = action.payload;
    },
    resetSearchKey: (state) => {
      state.searchKey = "";
    },
    resetJobCatogery: (state) => {
      state.category = "";
    },
    updateJobCatogery: (state, action) => {
      state.category = action.payload;
    },
    updateApiFailed: (state, action) => {
      state.isApiFailed = action.payload;
    },
    updateSelectedPeerGroup: (state, action) => {
      state.selectedPeerGroup = action.payload;
    },
    resetSelectedPeerGroup: (state) => {
      state.selectedPeerGroup = null;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(JobPricingListThunk.pending, (state, action) => {
      state.isListloading = true;
      state.isApiFailed = false;
    });
    builder.addCase(JobPricingListThunk.fulfilled, (state, action) => {
      state.list = action.payload.data;
      state.responseMessage = action.payload.responseMessage;
      state.isListloading = false;
    });

    builder.addCase(JobPricingListThunk.rejected, (state, action) => {
      state.isListloading = false;
      state.list = [];
      state.isApiFailed = true;
    });
    builder.addCase(getPricingFiltersThunk.pending, (state, action) => {
      state.isFiltersloading = true;
      state.filtersList = [];
      state.isApiFailed = false;
    });
    builder.addCase(getPricingFiltersThunk.fulfilled, (state, action) => {
      state.isFiltersloading = false;
      let allData = action.payload.data.data;
      if (allData?.jobfamily?.length > 0) {
        let allSubFamily: any = [];
        allData?.jobfamily?.map((ele: any) => {
          allSubFamily = [...allSubFamily, ...ele?.jobsubfamily];
        });
        allData["jobsubfamily"] = allSubFamily;
      }
      state.filtersList = allData;
    });
    builder.addCase(getPricingFiltersThunk.rejected, (state, action) => {
      state.isFiltersloading = false;
      state.filtersList = [];
      state.isApiFailed = true;
    });
  }
});

export const {
  resetJobPricingList,
  getFilters,
  resetFilters,
  updateFilters,
  updatedSearchKey,
  resetSearchKey,
  resetJobCatogery,
  updateJobCatogery,
  updateFiltersCount,
  updateApiFailed,
  resetFiltersCount,
  updatePeerGroupId,
  resetPeerGroupId,
  updateSelectedPeerGroup,
  resetSelectedPeerGroup
} = jobPricingListSlice.actions;

export default jobPricingListSlice.reducer;
